// ─── PHYSICAL CONTINUITY ────────────────────────────────────────────────────
//
// Haley, live 2026-08-09, quoting a Cruz reply back at me:
//
//   "He doesn't step closer, doesn't reach for her, just lets the space
//    between them sit there, honest."
//
//   "this showed me that cruz lost track of how they are physically touching
//    through the scene please make sure that is also 100% kept track of"
//
// WHAT ACTUALLY HAPPENED THAT NIGHT, for the record: that reply is downstream
// of the scene bug fixed in v242. Her message was orphaned into an empty
// `scene-1`, so Cruz was handed almost none of the diner-cruz scene — he did
// not forget where his hands were, he was never told. Full scene history
// (v242/v244/v245) is most of the cure.
//
// But it is not all of it. Across a 300-message scene a model will still lose
// the thread of who is holding whom, because contact is established once in
// paragraph four and never restated. Position is exactly the kind of fact that
// is expensive to re-derive from a long transcript and cheap to carry forward.
//
// WHAT THIS DOES, AND DELIBERATELY DOES NOT DO. It does not infer a state —
// no "they are currently embracing" flag, because that is a guess that goes
// stale and then lies confidently. It resurfaces the most recent physical
// SENTENCES verbatim, newest last, and lets the model read them. Quoting the
// page is honest; summarising it invents.

// ⚠️ PHYSICAL BEATS LIVE IN NARRATION, NOT IN SPEECH — and this is the whole
// difference between a useful block and a misleading one.
//
// The first version matched body-part vocabulary anywhere in the sentence. Run
// over the real diner-cruz scene it returned four "physical facts" that were
// all Cruz TEACHING KNIFE ANATOMY: "Up here, under the jaw, where the big
// vessels sit", "the inside of the thigh, the base of the skull". Told those
// were the current physical situation, a character would have written
// something grotesque. Speech ABOUT bodies is not contact BETWEEN bodies.
//
// So quoted dialogue is stripped before anything is tested, and what remains
// must contain both a contact/posture verb AND a person. Instructional speech
// leaves behind only its dialogue tag, which carries neither.
const DIALOGUE_SPAN_RX = /["“][^"”]*["”]/g;
const PERSON_RX = /\b(?:her|hers|him|his|she|he|they|them|their|you|your)\b/i;
const CONTACT_VERB_RX = new RegExp([
  '\\b(?:touch(?:es|ed|ing)?|hold(?:s|ing)?|held|grip(?:s|ped|ping)?|grab(?:s|bed|bing)?)\\b',
  '\\b(?:kiss(?:es|ed|ing)?|embrace[sd]?|hug(?:s|ged|ging)?|cradle[sd]?|tuck(?:s|ed)?)\\b',
  '\\b(?:pull(?:s|ed|ing)?|tug(?:s|ged)?|press(?:es|ed|ing)?|lean(?:s|ed|ing)?|settle[sd]?)\\b',
  '\\b(?:wrap(?:s|ped|ping)?|drape[sd]?|slide[sd]?|slid|find(?:s)?|rest(?:s|ed|ing)?)\\b',
  '\\b(?:sit(?:s|ting)?|sat|stand(?:s|ing)?|stood|kneel(?:s|ing)?|knelt|crouch(?:es|ed)?)\\b',
  '\\b(?:step(?:s|ped|ping)?|move[sd]?|cross(?:es|ed)?|reach(?:es|ed|ing)?|nudge[sd]?|scruff(?:s|ed)?)\\b',
].join('|'), 'i');

// Retained as a specificity signal — a beat naming a body part or a distance
// is more useful to carry forward than a bare "he moves".
const CONTACT_RX = new RegExp([
  // touch and hold
  '\\b(?:touch(?:es|ed|ing)?|hold(?:s|ing)?|held|grip(?:s|ped|ping)?|grab(?:s|bed|bing)?)\\b',
  '\\b(?:hand|hands|fingers?|thumb|palm|wrist|arm|arms|elbow)\\b',
  '\\b(?:waist|hip|hips|shoulder|shoulders|knee|lap|chest|back|neck|nape|jaw|chin|cheek)\\b',
  // the shape of contact
  '\\b(?:kiss(?:es|ed|ing)?|embrace[sd]?|hug(?:s|ged|ging)?|cradle[sd]?|tuck(?:s|ed)?)\\b',
  '\\b(?:pull(?:s|ed|ing)?|tug(?:s|ged)?|press(?:es|ed|ing)?|lean(?:s|ed|ing)?|settle[sd]?)\\b',
  '\\b(?:wrap(?:s|ped|ping)?|drape[sd]?|rest(?:s|ed|ing)?\\s+(?:against|on|in))\\b',
  // posture and position
  '\\b(?:sit(?:s|ting)?|sat|stand(?:s|ing)?|stood|kneel(?:s|ing)?|knelt|crouch(?:es|ed)?|lie[sd]?|lay|lying)\\b',
  '\\b(?:step(?:s|ped|ping)?|cross(?:es|ed)?\\s+the|move[sd]?\\s+(?:closer|away|back))\\b',
  // proximity, including its absence — "space between them" is a physical fact
  '\\b(?:closer|beside|behind|between\\s+them|apart|distance|space\\s+between|inches?|arm[\'’]s\\s+length)\\b',
].join('|'), 'i');

// Sentences that are only dialogue carry no physical information — a line of
// speech about hands is not a hand on a shoulder.
const MOSTLY_DIALOGUE_RX = /^["“][^"”]*["”][.,!?]?$/;

/** Split into sentences without breaking on the abbreviations RP prose uses. */
function sentencesOf(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+(?=[A-Z"“*])/)
    .map(s => s.trim())
    .filter(Boolean);
}

/**
 * The most recent physical beats on the page, oldest-first.
 *
 * `messages` is oldest-first; each needs { content, speaker }. Only the tail is
 * scanned — physical state older than the last stretch of the scene has almost
 * always been superseded, and quoting it would argue for a position that is no
 * longer true.
 *
 * Exported for tests.
 */
export function extractPhysicalBeats(messages, { scan = 14, max = 4, maxChars = 220 } = {}) {
  const tail = (messages || []).filter(Boolean).slice(-scan);
  const beats = [];
  // Walk newest-first so `max` keeps the LATEST beats, then reverse at the end.
  for (let i = tail.length - 1; i >= 0 && beats.length < max; i--) {
    const m = tail[i];
    const sentences = sentencesOf(m.content);
    for (let j = sentences.length - 1; j >= 0 && beats.length < max; j--) {
      const s = sentences[j];
      if (s.length < 15 || s.length > maxChars) continue;
      if (MOSTLY_DIALOGUE_RX.test(s)) continue;
      // Judge the NARRATION only. Everything inside quotation marks is speech,
      // and speech about bodies is not contact between bodies.
      const narration = s.replace(DIALOGUE_SPAN_RX, ' ').replace(/\s+/g, ' ').trim();
      if (narration.length < 12) continue;
      if (!CONTACT_VERB_RX.test(narration)) continue;
      if (!PERSON_RX.test(narration)) continue;
      if (!CONTACT_RX.test(narration)) continue;
      beats.push({ speaker: m.speaker || '', text: s });
    }
  }
  return beats.reverse();
}

/**
 * The prompt block. Empty string when the scene has no physical beats — a
 * conversation held across a table should not be told to track a grip that
 * does not exist.
 */
// Story mode has no scene walls, so a 40-message window can cross a
// time-skip and assert last-scene positions as "STILL TRUE" (Nicole, live
// 2026-08-30: "it isn't responding to current scene… responding to a past
// scene"). Cut the window at the newest skip marker before extracting.
const TIMESKIP_RX = /^\W{0,6}((about|roughly|nearly)\s+)?((an?|one|two|three|four|five|several|a few|\d+)\s+)?(moment|minute|hour|day|week|month|year)s?\s+(later|pass)|^\W{0,6}(the\s+)?(next|following)\s+(morning|day|night|evening|week)|^\W{0,6}later\s+that\s+(night|day|evening|week)/i;
export function trimAfterTimeskip(messages) {
  const list = (messages || []).filter(Boolean);
  for (let i = list.length - 1; i >= 0; i--) {
    const head = String(list[i]?.content || '').slice(0, 80);
    if (TIMESKIP_RX.test(head)) return list.slice(i);
  }
  return list;
}

export function buildPhysicalContinuityBlock(messages, opts = {}) {
  const beats = extractPhysicalBeats(messages, opts);
  if (!beats.length) return '';
  const lines = beats.map(b => `- ${b.speaker ? `${b.speaker}: ` : ''}${b.text}`).join('\n');
  return `\n\nPHYSICAL CONTINUITY — where everyone actually is, from the page itself. These are the most recent physical facts established in this scene, oldest first:\n${lines}\n\nThese are STILL TRUE. You do not get to quietly reset them: if you are holding her, you are still holding her until you let go on the page, and if there is space between you, someone has to cross it. Never narrate distance you have not created, or contact you have not made. When you change the physical situation, SHOW the change rather than assuming it.`;
}
